// modules/reporteFacturas/pages/ReporteFacturasPorCodigoPage.jsx
// Reporte de facturas repartido por los códigos de /facturacion-admin: cada línea de una factura apunta a un item, y cada item a un código de
// producción (actividad económica) y a un producto/servicio del SIN. Aquí se ve cuánto de cada factura corresponde a cada código.
// Las líneas antiguas (sin item) se agrupan en "Sin código" para que todo siga cuadrando con el total facturado.
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert, Box, Button, Chip, CircularProgress, InputAdornment, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import * as XLSX from "xlsx";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import { useAuth } from "../../../context/AuthContext";
import { fieldSx } from "../../egresos/components/formStyles";
import { getReportePorCodigo } from "../services/reporteFacturasService";

const MESES = [
  { value: "Todo", label: "Todo (año completo)" },
  { value: 1, label: "Enero" }, { value: 2, label: "Febrero" }, { value: 3, label: "Marzo" }, { value: 4, label: "Abril" },
  { value: 5, label: "Mayo" }, { value: 6, label: "Junio" }, { value: 7, label: "Julio" }, { value: 8, label: "Agosto" },
  { value: 9, label: "Septiembre" }, { value: 10, label: "Octubre" }, { value: 11, label: "Noviembre" }, { value: 12, label: "Diciembre" },
];
const SUCURSALES = ["La Paz", "Cochabamba", "Santa Cruz", "Todo"];
const DOCUMENTOS = [
  { value: "no", label: "Solo facturas" },
  { value: "si", label: "Solo pre-facturas" },
  { value: "todos", label: "Facturas y pre-facturas" },
];
const anioActual = new Date().getFullYear();
const ANIOS = Array.from({ length: 6 }, (_, i) => anioActual + 1 - i);

const normalizarSucursal = (value) => {
  const s = String(value || "").trim().toLowerCase();
  if (s.includes("santa cruz")) return "Santa Cruz";
  if (s.includes("cochabamba")) return "Cochabamba";
  if (s.includes("la paz") || s.includes("el alto")) return "La Paz";
  return "Todo";
};
const moneda = (v) => Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fecha = (v) => { const m = String(v || "").match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[3]}/${m[2]}/${m[1]}` : "-"; };

const cellSx = { py: 0.35, px: 0.8, fontSize: "0.75rem", verticalAlign: "middle", whiteSpace: "nowrap" };
const headSx = {
  ...cellSx, fontWeight: 800, fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: 0.3, color: "text.secondary",
  bgcolor: "background.paper", borderBottom: 2, borderColor: "divider",
};

const Resumen = ({ etiqueta, valor, color, nota }) => (
  <Box sx={{ flex: "1 1 190px", minWidth: 180, px: 1.5, py: 0.9, border: 1, borderColor: "divider", borderLeft: 3, borderLeftColor: color, bgcolor: "background.paper" }}>
    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4 }}>{etiqueta}</Typography>
    <Typography sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1.25, fontVariantNumeric: "tabular-nums" }}>{valor}</Typography>
    {nota && <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{nota}</Typography>}
  </Box>
);

const ReporteFacturasPorCodigoPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [sucursal, setSucursal] = useState(() => searchParams.get("sucursal") || normalizarSucursal(user?.sucursal));
  const [mes, setMes] = useState(() => { const m = searchParams.get("mes"); return !m ? new Date().getMonth() + 1 : m === "Todo" ? "Todo" : Number(m); });
  const [year, setYear] = useState(() => Number(searchParams.get("year")) || anioActual);
  const [preFac, setPreFac] = useState(() => (["no", "si", "todos"].includes(searchParams.get("preFac")) ? searchParams.get("preFac") : "no"));
  const [columnas, setColumnas] = useState("codigo"); // codigo | item
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filtroCodigo, setFiltroCodigo] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [pagina, setPagina] = useState(0);
  const [limite, setLimite] = useState(50);

  const generar = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getReportePorCodigo({ sucursal, year, mes, preFac });
      setData(res.data?.items ? res.data : res.data?.data || null);
      setFiltroCodigo("");
      setPagina(0);
    } catch (e) {
      setData(null);
      setError(e?.response?.data?.error || "No se pudo generar el reporte.");
    } finally {
      setLoading(false);
    }
  };
  // Al abrir la pantalla se genera con los filtros que llegan del índice
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { generar(); }, []);

  const items = useMemo(() => data?.items || [], [data]);
  const codigos = useMemo(() => data?.codigos || [], [data]);
  const totales = data?.totales || { facturas: 0, monto_total: 0, con_codigo: 0, sin_codigo: 0, con_diferencia: 0 };

  // Columnas del detalle: un código de producción, o un item, según el modo (solo los que tienen monto en lo consultado)
  const cols = useMemo(() => {
    if (columnas === "codigo") {
      return codigos.map((c) => ({ clave: c.cod_produccion, titulo: c.cod_produccion, sub: c.grupo || c.items.map((x) => x.item).slice(0, 1).join(""), dato: (i) => i.por_codigo[c.cod_produccion] || 0 }));
    }
    return codigos.flatMap((c) => c.items.filter((x) => x.monto).map((x) => ({ clave: `i${x.id}`, titulo: x.item, sub: `${c.cod_produccion}${x.cod_producto_servicio ? ` · ${x.cod_producto_servicio}` : ""}`, dato: (i) => i.por_item[x.id] || 0 })));
  }, [columnas, codigos]);

  const visibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return items.filter((i) => {
      if (filtroCodigo && !i.por_codigo[filtroCodigo]) return false;
      if (!q) return true;
      return [i.cliente, i.servicio, i.num_fac, i.cod, i.descripcion].join(" ").toLowerCase().includes(q);
    });
  }, [items, filtroCodigo, busqueda]);

  const totalesVisibles = useMemo(() => {
    const porCol = Object.fromEntries(cols.map((c) => [c.clave, visibles.reduce((a, i) => a + c.dato(i), 0)]));
    return { porCol, sin: visibles.reduce((a, i) => a + i.sin_codigo, 0), total: visibles.reduce((a, i) => a + i.monto_total, 0) };
  }, [cols, visibles]);

  const exportarExcel = () => {
    const hoja1 = codigos.flatMap((c) => [
      { "Código de producción": c.cod_produccion, Grupo: c.grupo, Item: "(total del código)", "Producto SIN": "", Facturas: c.facturas, Monto: c.monto, "% del total": totales.monto_total ? Math.round((c.monto / totales.monto_total) * 10000) / 100 : 0 },
      ...c.items.map((x) => ({ "Código de producción": c.cod_produccion, Grupo: c.grupo, Item: x.item, "Producto SIN": x.cod_producto_servicio, Facturas: "", Monto: x.monto, "% del total": totales.monto_total ? Math.round((x.monto / totales.monto_total) * 10000) / 100 : 0 })),
    ]);
    hoja1.push({ "Código de producción": "SIN CÓDIGO", Grupo: "líneas antiguas sin item", Item: "", "Producto SIN": "", Facturas: "", Monto: totales.sin_codigo, "% del total": totales.monto_total ? Math.round((totales.sin_codigo / totales.monto_total) * 10000) / 100 : 0 });
    hoja1.push({ "Código de producción": "TOTAL FACTURADO", Grupo: "", Item: "", "Producto SIN": "", Facturas: totales.facturas, Monto: totales.monto_total, "% del total": 100 });
    const hoja2 = visibles.map((i) => ({
      Fecha: fecha(i.fecha), Documento: i.tipo_doc, "N.º factura": i.num_fac || "", "Cód.": i.cod, Cliente: i.cliente, Servicio: i.servicio, Sucursal: i.sucursal, Periodo: `${i.mes} ${i.anio || ""}`.trim(),
      ...Object.fromEntries(cols.map((c) => [c.sub ? `${c.titulo} (${c.sub})` : c.titulo, c.dato(i) || ""])),
      "Sin código": i.sin_codigo || "", "Total factura": i.monto_total, "Revisar": i.diferencia ? "Total distinto de la suma de sus líneas" : "",
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(hoja1), "Resumen por código");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(hoja2), "Detalle");
    XLSX.writeFile(wb, `facturas_por_codigo_${year}_${mes === "Todo" ? "anual" : String(mes).padStart(2, "0")}.xlsx`);
  };

  const periodoTxt = `${mes === "Todo" ? "Año completo" : MESES.find((m) => m.value === mes)?.label} ${year}`;
  const pag = visibles.slice(pagina * limite, pagina * limite + limite);
  const pctDe = (n) => (totales.monto_total ? `${Math.round((n / totales.monto_total) * 1000) / 10} %` : "0 %");

  return (
    <DashboardLayout>
      <PageHeader title="Reporte de Facturas por Código de Producción" subtitle="Cuánto de cada factura corresponde a cada código de /facturacion-admin" backTo="/reporte-facturas" />
      <Box sx={{ p: 1 }}>
        <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />} sx={{ mb: 1, py: 0.25, fontSize: "0.8rem", alignItems: "center" }}>
          Cada línea de una factura usa un <b>item</b>, y cada item pertenece a un <b>código de producción</b> (actividad económica) de Facturación admin. Aquí se reparte el monto de
          cada factura entre esos códigos. Las líneas antiguas, de antes de los conceptos internos, no tienen item y van en <b>“Sin código”</b>, así el total siempre cuadra.
        </Alert>

        {/* ── Filtros ── */}
        <Paper variant="outlined" sx={{ p: 1, mb: 1, display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <TextField select size="small" label="Sucursal" value={sucursal} onChange={(e) => setSucursal(e.target.value)} sx={{ ...fieldSx, width: 150 }}>
            {SUCURSALES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Mes" value={mes} onChange={(e) => setMes(e.target.value)} sx={{ ...fieldSx, width: 170 }}>
            {MESES.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Año" value={year} onChange={(e) => setYear(Number(e.target.value))} sx={{ ...fieldSx, width: 100 }}>
            {ANIOS.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Documentos" value={preFac} onChange={(e) => setPreFac(e.target.value)} sx={{ ...fieldSx, width: 210 }}>
            {DOCUMENTOS.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </TextField>
          <Button size="small" variant="contained" startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />} onClick={generar} disabled={loading} sx={{ textTransform: "none", fontWeight: 700 }}>
            Generar
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportarExcel} disabled={!data || !items.length} sx={{ textTransform: "none" }}>
            Exportar Excel
          </Button>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError("")}>{error}</Alert>}

        {data && (
          <>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
              <Resumen etiqueta="Total facturado" valor={`Bs ${moneda(totales.monto_total)}`} color="#546e7a" nota={`${totales.facturas} documento${totales.facturas === 1 ? "" : "s"} · ${periodoTxt}`} />
              <Resumen etiqueta="Con código de producción" valor={`Bs ${moneda(totales.con_codigo)}`} color="#0b6e6e" nota={`${pctDe(totales.con_codigo)} del total`} />
              <Resumen etiqueta="Sin código" valor={`Bs ${moneda(totales.sin_codigo)}`} color="#c77700" nota={`${pctDe(totales.sin_codigo)} · líneas antiguas sin item`} />
            </Box>
            {totales.con_diferencia > 0 && (
              <Alert severity="warning" icon={<WarningAmberIcon fontSize="small" />} sx={{ mb: 1, py: 0 }}>
                {totales.con_diferencia} documento{totales.con_diferencia === 1 ? "" : "s"} tiene{totales.con_diferencia === 1 ? "" : "n"} un total distinto de la suma de sus líneas. La diferencia se cuenta en “Sin código” y se marca con ⚠ en el detalle.
              </Alert>
            )}

            {/* ── Resumen por código ── */}
            <Paper variant="outlined" sx={{ mb: 1, overflow: "hidden" }}>
              <Box sx={{ px: 1.25, py: 0.6, bgcolor: "action.hover", borderBottom: 1, borderColor: "divider", borderLeft: "3px solid", borderLeftColor: "primary.main", display: "flex", gap: 1, alignItems: "baseline" }}>
                <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase", color: "primary.main" }}>Resumen por código de producción</Typography>
                <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>Toca un código para ver solo sus facturas abajo</Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headSx}>Código</TableCell>
                      <TableCell sx={headSx}>Grupo de dosificación</TableCell>
                      <TableCell sx={{ ...headSx, whiteSpace: "normal" }}>Items</TableCell>
                      <TableCell sx={headSx} align="right">Facturas</TableCell>
                      <TableCell sx={headSx} align="right">Monto</TableCell>
                      <TableCell sx={headSx} align="right">% del total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {codigos.length === 0 ? (
                      <TableRow><TableCell colSpan={6} align="center" sx={{ ...cellSx, py: 2, color: "text.secondary" }}>Ninguna factura de este periodo usa items con código de producción.</TableCell></TableRow>
                    ) : codigos.map((c) => (
                      <TableRow key={c.cod_produccion} hover onClick={() => { setFiltroCodigo(filtroCodigo === c.cod_produccion ? "" : c.cod_produccion); setPagina(0); }}
                        sx={{ cursor: "pointer", bgcolor: filtroCodigo === c.cod_produccion ? (t) => alpha(t.palette.primary.main, 0.1) : undefined }}>
                        <TableCell sx={{ ...cellSx, fontFamily: "monospace", fontWeight: 800, fontSize: "0.82rem" }}>{c.cod_produccion}</TableCell>
                        <TableCell sx={{ ...cellSx, fontWeight: 700, whiteSpace: "normal" }}>{c.grupo || <Typography component="span" sx={{ color: "text.disabled", fontSize: "0.75rem" }}>sin grupo propio</Typography>}</TableCell>
                        <TableCell sx={{ ...cellSx, whiteSpace: "normal", maxWidth: 420 }}>
                          <Box sx={{ display: "flex", gap: 0.4, flexWrap: "wrap" }}>
                            {c.items.map((x) => (
                              <Tooltip key={x.id} title={`Producto SIN ${x.cod_producto_servicio || "-"} · Bs ${moneda(x.monto)}`}>
                                <Chip size="small" variant="outlined" label={`${x.item}: ${moneda(x.monto)}`} sx={{ height: 20, fontSize: 10.5 }} />
                              </Tooltip>
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell sx={cellSx} align="right">{c.facturas}</TableCell>
                        <TableCell sx={{ ...cellSx, fontWeight: 800, fontVariantNumeric: "tabular-nums" }} align="right">{moneda(c.monto)}</TableCell>
                        <TableCell sx={{ ...cellSx, color: "text.secondary" }} align="right">{pctDe(c.monto)}</TableCell>
                      </TableRow>
                    ))}
                    {codigos.length > 0 && (
                      <>
                        <TableRow>
                          <TableCell sx={{ ...cellSx, fontWeight: 700, color: "warning.dark" }} colSpan={2}>Sin código</TableCell>
                          <TableCell sx={{ ...cellSx, color: "text.secondary", whiteSpace: "normal" }}>líneas antiguas, sin item</TableCell>
                          <TableCell sx={cellSx} />
                          <TableCell sx={{ ...cellSx, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: "warning.dark" }} align="right">{moneda(totales.sin_codigo)}</TableCell>
                          <TableCell sx={{ ...cellSx, color: "text.secondary" }} align="right">{pctDe(totales.sin_codigo)}</TableCell>
                        </TableRow>
                        <TableRow sx={{ bgcolor: "action.hover" }}>
                          <TableCell sx={{ ...cellSx, fontWeight: 800 }} colSpan={3}>TOTAL FACTURADO</TableCell>
                          <TableCell sx={{ ...cellSx, fontWeight: 800 }} align="right">{totales.facturas}</TableCell>
                          <TableCell sx={{ ...cellSx, fontWeight: 800, fontVariantNumeric: "tabular-nums" }} align="right">{moneda(totales.monto_total)}</TableCell>
                          <TableCell sx={{ ...cellSx, fontWeight: 800 }} align="right">100 %</TableCell>
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {/* ── Detalle por factura ── */}
            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
              <Box sx={{ px: 1.25, py: 0.6, bgcolor: "action.hover", borderBottom: 1, borderColor: "divider", borderLeft: "3px solid", borderLeftColor: "primary.main", display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase", color: "primary.main" }}>Detalle por factura</Typography>
                <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{visibles.length} documento{visibles.length === 1 ? "" : "s"}</Typography>
                <ToggleButtonGroup size="small" exclusive value={columnas} onChange={(_, v) => v && setColumnas(v)} sx={{ bgcolor: "background.paper", "& .MuiToggleButton-root": { py: 0.15, px: 1.25, fontSize: 12, textTransform: "none", fontWeight: 700 } }}>
                  <ToggleButton value="codigo">Columnas por código</ToggleButton>
                  <ToggleButton value="item">Columnas por item</ToggleButton>
                </ToggleButtonGroup>
                {filtroCodigo && <Chip size="small" color="primary" label={`Código ${filtroCodigo}`} onDelete={() => { setFiltroCodigo(""); setPagina(0); }} deleteIcon={<FilterAltOffIcon />} sx={{ height: 22, fontWeight: 700 }} />}
                <Box sx={{ flex: 1 }} />
                <TextField
                  size="small" placeholder="Buscar cliente, servicio o N.º…" value={busqueda} onChange={(e) => { setBusqueda(e.target.value); setPagina(0); }}
                  sx={{ ...fieldSx, width: { xs: "100%", sm: 260 } }}
                  slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
                />
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headSx}>Fecha</TableCell>
                      <TableCell sx={headSx}>Documento</TableCell>
                      <TableCell sx={headSx}>Cliente</TableCell>
                      <TableCell sx={headSx}>Servicio</TableCell>
                      {cols.map((c) => (
                        <TableCell key={c.clave} sx={{ ...headSx, whiteSpace: "normal", minWidth: 96 }} align="right">
                          <Tooltip title={c.sub || ""}>
                            <span>
                              <span style={{ fontFamily: columnas === "codigo" ? "monospace" : undefined }}>{c.titulo}</span>
                              {columnas === "item" && <Typography component="span" sx={{ display: "block", fontSize: "0.62rem", fontWeight: 400, textTransform: "none", color: "text.disabled" }}>{c.sub}</Typography>}
                            </span>
                          </Tooltip>
                        </TableCell>
                      ))}
                      <TableCell sx={{ ...headSx, color: "warning.dark" }} align="right">Sin código</TableCell>
                      <TableCell sx={headSx} align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pag.length === 0 ? (
                      <TableRow><TableCell colSpan={6 + cols.length} align="center" sx={{ ...cellSx, py: 3, color: "text.secondary" }}>Ninguna factura coincide con el filtro.</TableCell></TableRow>
                    ) : pag.map((i) => (
                      <TableRow key={i.cod} hover>
                        <TableCell sx={cellSx}>{fecha(i.fecha)}</TableCell>
                        <TableCell sx={cellSx}>
                          <Chip size="small" variant={i.tipo_doc === "Factura" ? "filled" : "outlined"} color={i.tipo_doc === "Factura" ? "success" : "warning"} label={i.tipo_doc === "Factura" ? `Factura ${i.num_fac || ""}`.trim() : `Pre-fac. ${i.cod}`} sx={{ height: 20, fontSize: 11 }} />
                        </TableCell>
                        <TableCell sx={{ ...cellSx, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", fontWeight: 700 }} title={i.cliente}>{i.cliente}</TableCell>
                        <TableCell sx={cellSx}>{i.servicio}</TableCell>
                        {cols.map((c) => {
                          const v = c.dato(i);
                          return <TableCell key={c.clave} sx={{ ...cellSx, fontVariantNumeric: "tabular-nums", color: v ? "text.primary" : "text.disabled" }} align="right">{v ? moneda(v) : "·"}</TableCell>;
                        })}
                        <TableCell sx={{ ...cellSx, fontVariantNumeric: "tabular-nums", color: i.sin_codigo ? "warning.dark" : "text.disabled" }} align="right">
                          {i.sin_codigo ? moneda(i.sin_codigo) : "·"}
                          {i.diferencia ? <Tooltip title={`El total de la factura difiere de la suma de sus líneas en ${moneda(i.diferencia)}`}><WarningAmberIcon sx={{ fontSize: 14, ml: 0.3, verticalAlign: "text-bottom", color: "warning.main" }} /></Tooltip> : null}
                        </TableCell>
                        <TableCell sx={{ ...cellSx, fontWeight: 800, fontVariantNumeric: "tabular-nums" }} align="right">{moneda(i.monto_total)}</TableCell>
                      </TableRow>
                    ))}
                    {visibles.length > 0 && (
                      <TableRow sx={{ bgcolor: "action.hover" }}>
                        <TableCell colSpan={4} sx={{ ...cellSx, fontWeight: 800 }} align="right">Total de lo filtrado ({visibles.length}):</TableCell>
                        {cols.map((c) => <TableCell key={c.clave} sx={{ ...cellSx, fontWeight: 800, fontVariantNumeric: "tabular-nums" }} align="right">{moneda(totalesVisibles.porCol[c.clave])}</TableCell>)}
                        <TableCell sx={{ ...cellSx, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: "warning.dark" }} align="right">{moneda(totalesVisibles.sin)}</TableCell>
                        <TableCell sx={{ ...cellSx, fontWeight: 800, fontVariantNumeric: "tabular-nums" }} align="right">{moneda(totalesVisibles.total)}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div" count={visibles.length} page={pagina} rowsPerPage={limite} rowsPerPageOptions={[25, 50, 100, 200]}
                onPageChange={(_, p) => setPagina(p)} onRowsPerPageChange={(e) => { setLimite(Number(e.target.value)); setPagina(0); }}
                labelRowsPerPage="Filas" labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                sx={{ borderTop: 1, borderColor: "divider", "& .MuiTablePagination-toolbar": { minHeight: 40 } }}
              />
            </Paper>
          </>
        )}
      </Box>
    </DashboardLayout>
  );
};

export default ReporteFacturasPorCodigoPage;
